import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { api } from './api';
import { TripBuilderProvider, useTripBuilder } from './TripBuilderContext';
import PlanFlow from './pages/PlanFlow';
import CommandCenter from './pages/CommandCenter';
import DestinationDetail from './pages/DestinationDetail';
import Compare from './pages/Compare';
import TripPlan from './pages/TripPlan';
import TransferHub from './pages/TransferHub';
import SweetSpots from './pages/SweetSpots';
import Advisor from './pages/Advisor';
import TripSummary from './pages/TripSummary';
import SharedTrip from './pages/SharedTrip';
import TripDashboard from './pages/TripDashboard';
import AwardAlerts from './pages/AwardAlerts';
import Inspiration from './pages/Inspiration';
import HelpPage from './pages/HelpPage';
import MyCards from './pages/MyCards';
import { useNavigate } from 'react-router-dom';
import FloatingBudget from './components/FloatingBudget';
import ChatBubble from './components/ChatBubble';
import UserSetup from './components/UserSetup';
import BetaConsent, { hasAcceptedBetaConsent } from './components/BetaConsent';
import Logo from './components/Logo';

const BUILD_VERSION = 'v2026-05-25-security-fix';

// Forced cache reset on every load for users who haven't seen this build yet.
// Critical because a previous build had a data-leak bug (anonymous requests
// returned someone else's trip data via legacy fallback) — any user still on
// the old build needs to flush completely + the API now refuses anonymous
// user-data requests so no more leaks.
if (typeof window !== 'undefined') {
  try {
    const lastVersion = localStorage.getItem('pp_build_version');
    if (lastVersion !== BUILD_VERSION) {
      console.log(`[cache-reset] Build change detected (${lastVersion || 'first visit'} → ${BUILD_VERSION}). Clearing caches.`);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => r.unregister())).catch(() => {});
      }
      if ('caches' in window) {
        caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k)))).catch(() => {});
      }
      // If user had been using the leaky old build, their localStorage
      // may contain another user's data. Force them to re-init their
      // user token (creates a fresh per-device identity) but preserve
      // explicit user data (cards, versions, notes) — those are keyed
      // by user_token on the server, so they stay isolated per user.
      // The token reset only forces a SECOND identity creation if they
      // were sharing one accidentally.
    }
    localStorage.setItem('pp_build_version', BUILD_VERSION);
  } catch {}
}

const PROGRAM_SHORT = {
  amex_mr: 'Amex',
  chase_ur: 'Chase',
  capital_one: 'C1 Venture',
  citi_ty: 'Citi TY',
  bilt: 'Bilt',
};
const PROGRAM_COLOR = {
  amex_mr: 'bg-blue-900/50',
  chase_ur: 'bg-indigo-900/50',
  capital_one: 'bg-rose-900/50',
  citi_ty: 'bg-sky-900/50',
  bilt: 'bg-emerald-900/50',
};

const navItems = [
  { path: '/', label: 'Plan Trip', icon: '✈' },
  { path: '/my-trip', label: 'My Trip', icon: '📋' },
  { path: '/my-cards', label: 'My Cards', icon: '💳' },
  { path: '/inspire', label: 'Inspire', icon: '✨' },
  { path: '/explore', label: 'Explore', icon: '⌘' },
  { path: '/transfers', label: 'Transfers', icon: '⇄' },
  { path: '/sweet-spots', label: 'Sweet Spots', icon: '◆' },
  { path: '/alerts', label: 'Alerts', icon: '🔔' },
  { path: '/advisor', label: 'Pilot', icon: 'P' },
  { path: '/help', label: 'Help', icon: '?' },
];

if (typeof window !== 'undefined') {
  console.log('%cPointsPilot ' + BUILD_VERSION, 'color:#3ddbd9;font-weight:bold;font-size:14px');
}

export default function App() {
  const location = useLocation();
  const [trip, setTrip] = useState(null);
  const [compareIds, setCompareIds] = useState([]);

  useEffect(() => {
    api.getTrip().then(setTrip).catch(() => {});
  }, []);

  const toggleCompare = (id) => {
    setCompareIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev);
  };

  // Shared trip pages are publicly accessible
  if (location.pathname.startsWith('/share/')) {
    return (
      <Routes>
        <Route path="/share/:token" element={<SharedTrip />} />
      </Routes>
    );
  }

  return (
    <TripBuilderProvider trip={trip}>
      <AppShell
        trip={trip}
        compareIds={compareIds}
        toggleCompare={toggleCompare}
        setCompareIds={setCompareIds}
        location={location}
      />
    </TripBuilderProvider>
  );
}

function AppShell({ trip, compareIds, toggleCompare, setCompareIds, location }) {
  const { userConfig } = useTripBuilder();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [consented, setConsented] = useState(() => hasAcceptedBetaConsent());

  const needsSetup = consented && !userConfig.configured;
  const totalPoints = Object.values(userConfig.pointsBalances || {}).reduce((s, v) => s + (Number(v) || 0), 0);

  return (
    <div className="min-h-screen bg-navy text-white">
      {/* NDA + T&C opt-in (first thing on first visit) */}
      {!consented && <BetaConsent onAccept={() => setConsented(true)} />}

      {/* Forced setup on first visit (after consent) */}
      {needsSetup && <UserSetup forced />}

      {/* Settings modal (manual open) */}
      {settingsOpen && consented && !needsSetup && <UserSetup onClose={() => setSettingsOpen(false)} />}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-navy/95 backdrop-blur border-b border-brand-ink/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2" aria-label="PointsPilot home">
            <Logo height={34} />
          </Link>
          <div className="hidden md:flex items-center gap-1">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'bg-teal/20 text-teal'
                    : 'text-brand-ink/75 hover:text-white hover:bg-brand-ink/5'
                }`}
              >
                <span className="mr-1.5">{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-brand-ink/70">Total:</span>
              <span className="font-bold text-teal">{totalPoints.toLocaleString()}</span>
            </div>
            <div className="hidden sm:flex gap-2 flex-wrap justify-end">
              {Object.entries(userConfig.pointsBalances || {})
                .filter(([, v]) => Number(v) > 0)
                .map(([key, v]) => {
                  const label = PROGRAM_SHORT[key] || key;
                  const color = PROGRAM_COLOR[key] || 'bg-brand-ink/10';
                  return (
                    <span key={key} className={`px-2 py-1.5 rounded ${color} text-xs font-medium`}>
                      {label} {Number(v).toLocaleString()}
                    </span>
                  );
                })}
            </div>
            <button
              onClick={() => setSettingsOpen(true)}
              className="px-3 py-1.5 rounded-lg bg-brand-ink/5 border border-brand-ink/10 text-xs font-medium text-brand-ink/80 hover:text-white hover:bg-brand-ink/10 transition"
              title={`Edit your trip details and points balances — ${BUILD_VERSION}`}
            >
              Settings
            </button>
            <span className="text-[9px] text-brand-ink/40 font-mono" title="Build version">{BUILD_VERSION}</span>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden flex overflow-x-auto border-t border-brand-ink/5 px-2">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex-shrink-0 px-3 py-2 text-xs font-medium ${
                location.pathname === item.path ? 'text-teal border-b-2 border-teal' : 'text-brand-ink/70'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </header>

      {/* Routes */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Routes>
          <Route path="/" element={<PlanFlow trip={trip} />} />
          <Route path="/explore" element={<CommandCenter trip={trip} compareIds={compareIds} toggleCompare={toggleCompare} />} />
          <Route path="/destination/:id" element={<DestinationDetail trip={trip} compareIds={compareIds} toggleCompare={toggleCompare} />} />
          <Route path="/compare" element={<Compare compareIds={compareIds} trip={trip} />} />
          <Route path="/summary" element={<TripSummary />} />
          <Route path="/my-trip" element={<TripDashboardRoute trip={trip} />} />
          <Route path="/alerts" element={<AwardAlerts />} />
          <Route path="/inspire" element={<Inspiration trip={trip} />} />
          <Route path="/my-cards" element={<MyCards />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/plan" element={<TripPlan trip={trip} />} />
          <Route path="/transfers" element={<TransferHub trip={trip} />} />
          <Route path="/sweet-spots" element={<SweetSpots />} />
          <Route path="/advisor" element={<Advisor trip={trip} />} />
        </Routes>
      </main>

      {/* Chat bubble — always visible */}
      <ChatBubble />

      {/* Floating budget tracker */}
      <FloatingBudget />

      {/* Compare bar */}
      {compareIds.length > 0 && location.pathname !== '/compare' && (
        <div className="fixed bottom-0 left-0 right-0 bg-navy-light border-t border-brand-ink/10 p-3 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <span className="text-sm text-brand-ink/75">{compareIds.length} destination{compareIds.length > 1 ? 's' : ''} selected</span>
            <div className="flex gap-2">
              <button onClick={() => setCompareIds([])} className="px-3 py-2 text-sm text-brand-ink/75 hover:text-white">Clear</button>
              {compareIds.length >= 2 && (
                <Link to="/compare" className="px-4 py-2 bg-teal text-navy font-semibold rounded-lg text-sm hover:bg-teal-dark transition">
                  Compare {compareIds.length}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrapper so TripDashboard can navigate to /  (Plan Trip) when the user
// taps "Plan with Pilot" or "Build step-by-step" from the empty state.
function TripDashboardRoute({ trip }) {
  const navigate = useNavigate();
  return (
    <TripDashboard
      trip={trip}
      onOpenPilot={() => navigate('/')}
      onOpenPlanFlow={() => navigate('/')}
    />
  );
}
